import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Badge, Card, PageHeader, SelectInput, TextInput, useEntityList } from "@pericles/framework-frontend";

import { clearWorkspaceOpenAiSecret, saveWorkspaceOpenAiSecret } from "../api/workspaceSecretsClient";
import { generateSimpleAppArchetype } from "../api/archetypeClient";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import { useOpenAiSecretStatus } from "../hooks/useOpenAiSecretStatus";
import { useInternalCapabilities } from "../hooks/internalCapabilities";
import type { ArchetypeGenerationResponse } from "../types/archetype";
import {
  createRoadmapProject,
  fetchRoadmapProjects,
  fetchRoadmapMembers,
  fetchRoadmapSettings,
  removeRoadmapMember,
  saveRoadmapMember,
  saveRoadmapSettings,
  type RoadmapMember,
  type RoadmapProject,
  type RoadmapRole,
  type RoadmapRolePermissions,
  type RoadmapUser,
  type RoadmapSettings,
} from "../api/roadmapClient";

import { InternalAdminPanel } from "./InternalAdminScreen";
import { PageShell } from "./ui";

type CustomerListItem = { id: string | number; name?: string };

function SecretStatusBadge({ configured }: { configured: boolean }): JSX.Element {
  return configured ? <Badge label="Configured" variant="success" /> : <Badge label="Missing" variant="danger" />;
}

function InternalAdminSection(): JSX.Element {
  const { internalAdminEnabled, isLoading, error } = useInternalCapabilities();

  return (
    <div id="internal-admin">
      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div>
            <div className="text-base font-semibold text-slate-900">Internal Admin</div>
            <p className="text-sm text-slate-700">
              Provision tenants and owners. Requires ENABLE_INTERNAL_ADMIN and ADMIN_SECRET.
            </p>
          </div>
          {isLoading ? (
            <div className="text-sm text-slate-600">Loading internal admin capabilities...</div>
          ) : null}
          {!isLoading && !internalAdminEnabled ? (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">Internal admin disabled</div>
              <p className="mt-1">
                To enable, set <span className="font-mono">ENABLE_INTERNAL_ADMIN=1</span> and{" "}
                <span className="font-mono">ADMIN_SECRET</span> on the backend, then restart the server.
              </p>
            </div>
          ) : null}
          <InternalAdminPanel />
          {error ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">{error}</div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

export function WorkspaceSettingsScreen(): JSX.Element {
  const location = useLocation();
  const [inputValue, setInputValue] = useState<string>("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { status, isLoading, error: loadError, refreshStatus, setStatus } = useOpenAiSecretStatus(DEFAULT_WORKSPACE_ID);
  const [isSaving, setIsSaving] = useState(false);
  const [appNameInput, setAppNameInput] = useState<string>("Studio Archetype App");
  const [appDescriptionInput, setAppDescriptionInput] = useState<string>("Sample archetype generated from Studio");
  const [archetypeResult, setArchetypeResult] = useState<ArchetypeGenerationResponse | null>(null);
  const [archetypeError, setArchetypeError] = useState<string | null>(null);
  const [isGeneratingArchetype, setIsGeneratingArchetype] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [newProjectName, setNewProjectName] = useState<string>("");
  const [airtableBaseId, setAirtableBaseId] = useState("");
  const [airtableTokenInput, setAirtableTokenInput] = useState("");
  const [airtableStoredToken, setAirtableStoredToken] = useState("");
  const [airtableProcessesTable, setAirtableProcessesTable] = useState("");
  const [airtableProcessesView, setAirtableProcessesView] = useState("");
  const [airtableUseCasesTable, setAirtableUseCasesTable] = useState("");
  const [airtableUseCasesView, setAirtableUseCasesView] = useState("");
  const [airtableUsersTable, setAirtableUsersTable] = useState("");
  const [airtableUsersView, setAirtableUsersView] = useState("");
  const [airtableAppsTable, setAirtableAppsTable] = useState("");
  const [airtableAppsView, setAirtableAppsView] = useState("");
  const [airtableIntegrationsTable, setAirtableIntegrationsTable] = useState("");
  const [airtableIntegrationsView, setAirtableIntegrationsView] = useState("");
  const [airtableTechnicalAssessmentsTable, setAirtableTechnicalAssessmentsTable] = useState("");
  const [airtableTechnicalAssessmentsView, setAirtableTechnicalAssessmentsView] = useState("");
  const [airtableDataRiskChecklistsTable, setAirtableDataRiskChecklistsTable] = useState("");
  const [airtableDataRiskChecklistsView, setAirtableDataRiskChecklistsView] = useState("");
  const [airtableSaveMessage, setAirtableSaveMessage] = useState<string | null>(null);
  const [airtableError, setAirtableError] = useState<string | null>(null);
  const [roadmapMembers, setRoadmapMembers] = useState<RoadmapMember[]>([]);
  const [roadmapUsers, setRoadmapUsers] = useState<RoadmapUser[]>([]);
  const [roadmapRolePermissions, setRoadmapRolePermissions] = useState<Record<RoadmapRole, RoadmapRolePermissions>>(
    {} as Record<RoadmapRole, RoadmapRolePermissions>,
  );
  const [roadmapRoleOptions, setRoadmapRoleOptions] = useState<RoadmapRole[]>([]);
  const [selectedMemberUserId, setSelectedMemberUserId] = useState<string>("");
  const [selectedMemberRole, setSelectedMemberRole] = useState<RoadmapRole>("VIEWER");
  const [memberSaveMessage, setMemberSaveMessage] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = (customerOptionsData ?? [])
    .map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (location.hash !== "#internal-admin") return;
    if (typeof document === "undefined") return;
    const element = document.getElementById("internal-admin");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      setSelectedProjectId("");
      return;
    }
    let isActive = true;
    fetchRoadmapProjects(selectedCustomerId)
      .then((loaded) => {
        if (!isActive) return;
        setProjects(loaded);
        if (loaded.length && !loaded.find((project) => project.id === selectedProjectId)) {
          setSelectedProjectId(loaded[0].id);
        }
      })
      .catch((loadError) => {
        console.error(loadError);
        if (!isActive) return;
        setAirtableError("Impossible de charger les projets de roadmap.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedCustomerId || !selectedProjectId) return;
    let isActive = true;
    fetchRoadmapSettings(selectedProjectId)
      .then((settings) => {
        if (!isActive) return;
        if (!settings) {
          setAirtableBaseId("");
          setAirtableTokenInput("");
          setAirtableStoredToken("");
          setAirtableProcessesTable("");
          setAirtableProcessesView("");
          setAirtableUseCasesTable("");
          setAirtableUseCasesView("");
          setAirtableUsersTable("");
          setAirtableUsersView("");
          setAirtableAppsTable("");
          setAirtableAppsView("");
          setAirtableIntegrationsTable("");
          setAirtableIntegrationsView("");
          setAirtableTechnicalAssessmentsTable("");
          setAirtableTechnicalAssessmentsView("");
          setAirtableDataRiskChecklistsTable("");
          setAirtableDataRiskChecklistsView("");
          return;
        }
        setAirtableBaseId(settings.baseId ?? "");
        setAirtableStoredToken(settings.hasToken ? "stored" : "");
        setAirtableTokenInput("");
        setAirtableProcessesTable(settings.processesTable ?? "");
        setAirtableProcessesView(settings.processesView ?? "");
        setAirtableUseCasesTable(settings.useCasesTable ?? "");
        setAirtableUseCasesView(settings.useCasesView ?? "");
        setAirtableUsersTable(settings.usersTable ?? "");
        setAirtableUsersView(settings.usersView ?? "");
        setAirtableAppsTable(settings.appsTable ?? "");
        setAirtableAppsView(settings.appsView ?? "");
        setAirtableIntegrationsTable(settings.integrationsTable ?? "");
        setAirtableIntegrationsView(settings.integrationsView ?? "");
        setAirtableTechnicalAssessmentsTable(settings.technicalAssessmentsTable ?? "");
        setAirtableTechnicalAssessmentsView(settings.technicalAssessmentsView ?? "");
        setAirtableDataRiskChecklistsTable(settings.dataRiskChecklistsTable ?? "");
        setAirtableDataRiskChecklistsView(settings.dataRiskChecklistsView ?? "");
      })
      .catch((loadError) => {
        console.error(loadError);
        if (!isActive) return;
        setAirtableError("Impossible de charger les paramètres Airtable.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setRoadmapMembers([]);
      setRoadmapUsers([]);
      setRoadmapRolePermissions({} as Record<RoadmapRole, RoadmapRolePermissions>);
      setRoadmapRoleOptions([]);
      setSelectedMemberUserId("");
      setSelectedMemberRole("VIEWER");
      setMemberSaveMessage(null);
      setMemberError(null);
      return;
    }
    let isActive = true;
    fetchRoadmapMembers(selectedProjectId)
      .then((payload) => {
        if (!isActive) return;
        setRoadmapMembers(payload.members);
        setRoadmapUsers(payload.users);
        setRoadmapRolePermissions(payload.rolePermissions);
        setRoadmapRoleOptions(payload.roleOptions);
        setMemberError(null);
      })
      .catch((loadError) => {
        console.error(loadError);
        if (!isActive) return;
        setMemberError("Impossible de charger les membres du projet.");
        setRoadmapMembers([]);
        setRoadmapUsers([]);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  const handleAddRoadmapProject = async () => {
    if (!selectedCustomerId) {
      setAirtableError("Sélectionnez un client avant de créer un projet.");
      return;
    }
    if (!newProjectName.trim()) {
      setAirtableError("Le nom du projet est requis.");
      return;
    }
    try {
      const newProject = await createRoadmapProject(selectedCustomerId, newProjectName.trim());
      setProjects((current) => [newProject, ...current]);
      setSelectedProjectId(newProject.id);
      setNewProjectName("");
      setAirtableError(null);
    } catch (createError) {
      console.error(createError);
      setAirtableError("Impossible de créer le projet de roadmap.");
    }
  };

  const handleSaveAirtableSettings = async () => {
    if (!selectedCustomerId || !selectedProjectId) {
      setAirtableError("Sélectionnez un client et un projet.");
      return;
    }
    const tokenToSave = airtableTokenInput.trim();
    if (!airtableBaseId.trim() || (!tokenToSave && !airtableStoredToken)) {
      setAirtableError("Base ID et token Airtable sont requis.");
      return;
    }
    try {
      const settings: RoadmapSettings = await saveRoadmapSettings(selectedProjectId, {
        baseId: airtableBaseId.trim(),
        processesTable: airtableProcessesTable.trim(),
        processesView: airtableProcessesView.trim(),
        useCasesTable: airtableUseCasesTable.trim(),
        useCasesView: airtableUseCasesView.trim(),
        usersTable: airtableUsersTable.trim(),
        usersView: airtableUsersView.trim(),
        appsTable: airtableAppsTable.trim(),
        appsView: airtableAppsView.trim(),
        integrationsTable: airtableIntegrationsTable.trim(),
        integrationsView: airtableIntegrationsView.trim(),
        technicalAssessmentsTable: airtableTechnicalAssessmentsTable.trim(),
        technicalAssessmentsView: airtableTechnicalAssessmentsView.trim(),
        dataRiskChecklistsTable: airtableDataRiskChecklistsTable.trim(),
        dataRiskChecklistsView: airtableDataRiskChecklistsView.trim(),
        ...(tokenToSave ? { apiToken: tokenToSave } : {}),
      });
      setAirtableSaveMessage("Paramètres Airtable enregistrés pour ce projet.");
      setAirtableStoredToken(settings.hasToken ? "stored" : "");
      setAirtableTokenInput("");
      setAirtableError(null);
    } catch (saveError) {
      console.error(saveError);
      setAirtableError("Impossible d'enregistrer les paramètres Airtable.");
    }
  };

  const handleSaveRoadmapMember = async (userId: string, role: RoadmapRole) => {
    if (!selectedProjectId) {
      setMemberError("Sélectionnez un projet.");
      return;
    }
    try {
      const saved = await saveRoadmapMember(selectedProjectId, userId, role);
      setRoadmapMembers((current) => {
        const next = current.filter((member) => member.userId !== saved.userId);
        return [...next, saved].sort((a, b) => a.email.localeCompare(b.email));
      });
      setMemberSaveMessage("Membre mis à jour.");
      setMemberError(null);
    } catch (saveError) {
      console.error(saveError);
      setMemberError("Impossible d'enregistrer le membre.");
    }
  };

  const handleAddRoadmapMember = async () => {
    if (!selectedMemberUserId) {
      setMemberError("Sélectionnez un membre.");
      return;
    }
    await handleSaveRoadmapMember(selectedMemberUserId, selectedMemberRole);
    setSelectedMemberUserId("");
  };

  const handleRemoveRoadmapMember = async (userId: string) => {
    if (!selectedProjectId) return;
    try {
      await removeRoadmapMember(selectedProjectId, userId);
      setRoadmapMembers((current) => current.filter((member) => member.userId !== userId));
      setMemberSaveMessage("Membre retiré.");
      setMemberError(null);
    } catch (removeError) {
      console.error(removeError);
      setMemberError("Impossible de retirer le membre.");
    }
  };

  const assignedMemberIds = new Set(roadmapMembers.map((member) => member.userId));
  const availableUsers = roadmapUsers.filter((user) => !assignedMemberIds.has(user.id));
  const roleOptions: RoadmapRole[] = roadmapRoleOptions.length
    ? roadmapRoleOptions
    : ["VIEWER", "EDITOR", "REVIEWER", "ADMIN"];

  const handleSave = async () => {
    if (!inputValue.trim()) {
      setError("Enter a valid OpenAI API key before saving.");
      return;
    }
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const nextStatus = await saveWorkspaceOpenAiSecret(DEFAULT_WORKSPACE_ID, inputValue.trim());
      setStatus(nextStatus);
      setInputValue("");
      setMessage("OpenAI API key saved. It will be used ahead of environment variables.");
    } catch (saveError) {
      const messageText = saveError instanceof Error ? saveError.message : "Failed to save OpenAI API key";
      setError(messageText);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const nextStatus = await clearWorkspaceOpenAiSecret(DEFAULT_WORKSPACE_ID);
      setStatus(nextStatus);
      setInputValue("");
      setMessage("OpenAI API key cleared for this workspace.");
    } catch (clearError) {
      const messageText = clearError instanceof Error ? clearError.message : "Failed to clear OpenAI API key";
      setError(messageText);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateArchetype = async () => {
    setIsGeneratingArchetype(true);
    setArchetypeError(null);
    try {
      const result = await generateSimpleAppArchetype({
        appName: appNameInput,
        description: appDescriptionInput,
      });
      setArchetypeResult(result);
    } catch (generateError) {
      const messageText = generateError instanceof Error ? generateError.message : "Failed to generate archetype";
      setArchetypeError(messageText);
      setArchetypeResult(null);
    } finally {
      setIsGeneratingArchetype(false);
    }
  };

  return (
    <PageShell className="p-4">
      <PageHeader title="Workspace settings" subtitle="Manage secrets used by Jarvis runs and benches." />
      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-base font-semibold text-slate-900">OpenAI API key</div>
              <p className="text-sm text-slate-700">Stored as a secret. Never rendered in full.</p>
            </div>
            <button type="button" className="button button-ghost button-sm" onClick={() => void refreshStatus()} disabled={isLoading}>
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-900">Status</div>
              <SecretStatusBadge configured={Boolean(status?.configured)} />
            </div>

            {status?.previewMasked ? (
              <div className="mt-2 text-xs text-slate-700">
                Masked preview: <span className="font-mono">{status.previewMasked}</span>
                {status.updatedAt ? ` (updated ${new Date(status.updatedAt).toLocaleString()})` : null}
              </div>
            ) : (
              <div className="mt-2 text-xs text-slate-600">No key stored yet. Environment variables will be used if set.</div>
            )}

            <div className="mt-3 flex flex-col gap-2 text-sm text-slate-800">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">New key (never displayed)</label>
              <TextInput
                type="password"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder="sk-..."
                autoComplete="off"
              />
              <div className="flex flex-wrap gap-2">
                <button type="button" className="button button-primary" disabled={isSaving} onClick={() => void handleSave()}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={isSaving || !status?.configured}
                  onClick={() => void handleClear()}
                >
                  Clear
                </button>
              </div>
              <div className="text-xs text-slate-600">
                Priority: Studio secret → server environment (.env) → failure with guidance if missing.
              </div>
            </div>
          </div>

          {message ? <div className="rounded border border-green-200 bg-green-50 p-2 text-sm text-green-800">{message}</div> : null}
          {error || loadError ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-800">{error ?? loadError}</div>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div>
            <div className="text-base font-semibold text-slate-900">Atlas settings</div>
            <p className="text-sm text-slate-700">
              Paramétrez les accès Airtable par client et par projet de roadmap.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Client
              <SelectInput
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
              >
                <option value="">Sélectionner…</option>
                {customerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Projet de roadmap
              <SelectInput
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={!projects.length}
              >
                <option value="">Sélectionner…</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </SelectInput>
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Nouveau projet
              <TextInput
                value={newProjectName}
                onChange={(event) => setNewProjectName(event.target.value)}
                placeholder="Roadmap IA 2024"
              />
            </label>
            <button type="button" className="button button-secondary button-sm" onClick={handleAddRoadmapProject}>
              Créer le projet
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Airtable Base ID
              <TextInput
                value={airtableBaseId}
                onChange={(event) => setAirtableBaseId(event.target.value)}
                placeholder="appXXXXXXXXXXXXXX"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Airtable API Token
              <TextInput
                type="password"
                value={airtableTokenInput}
                onChange={(event) => setAirtableTokenInput(event.target.value)}
                placeholder={airtableStoredToken ? "••••••••••" : "pat..."}
              />
            </label>
            <div className="flex items-center gap-2 text-xs text-slate-600 md:col-span-2">
              <Badge label={airtableStoredToken ? "Token enregistré" : "Token manquant"} variant={airtableStoredToken ? "success" : "danger"} />
              {airtableStoredToken ? "Le token est stocké et masqué." : "Ajoutez un token pour importer depuis Airtable."}
            </div>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Processes (RI-7)
              <TextInput
                value={airtableProcessesTable}
                onChange={(event) => setAirtableProcessesTable(event.target.value)}
                placeholder="Processes"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Processes (optionnel)
              <TextInput
                value={airtableProcessesView}
                onChange={(event) => setAirtableProcessesView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Use Cases (RI-8)
              <TextInput
                value={airtableUseCasesTable}
                onChange={(event) => setAirtableUseCasesTable(event.target.value)}
                placeholder="Use Cases"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Use Cases (optionnel)
              <TextInput
                value={airtableUseCasesView}
                onChange={(event) => setAirtableUseCasesView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Users (optionnel)
              <TextInput
                value={airtableUsersTable}
                onChange={(event) => setAirtableUsersTable(event.target.value)}
                placeholder="Users"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Users (optionnel)
              <TextInput
                value={airtableUsersView}
                onChange={(event) => setAirtableUsersView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Apps (optionnel)
              <TextInput
                value={airtableAppsTable}
                onChange={(event) => setAirtableAppsTable(event.target.value)}
                placeholder="Applications"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Apps (optionnel)
              <TextInput
                value={airtableAppsView}
                onChange={(event) => setAirtableAppsView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Integrations (optionnel)
              <TextInput
                value={airtableIntegrationsTable}
                onChange={(event) => setAirtableIntegrationsTable(event.target.value)}
                placeholder="Integrations"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Integrations (optionnel)
              <TextInput
                value={airtableIntegrationsView}
                onChange={(event) => setAirtableIntegrationsView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Technical Assessments (optionnel)
              <TextInput
                value={airtableTechnicalAssessmentsTable}
                onChange={(event) => setAirtableTechnicalAssessmentsTable(event.target.value)}
                placeholder="Technical Assessments"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Technical Assessments (optionnel)
              <TextInput
                value={airtableTechnicalAssessmentsView}
                onChange={(event) => setAirtableTechnicalAssessmentsView(event.target.value)}
                placeholder="All records"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Table Data Risk Checklists (optionnel)
              <TextInput
                value={airtableDataRiskChecklistsTable}
                onChange={(event) => setAirtableDataRiskChecklistsTable(event.target.value)}
                placeholder="Data Risk Checklists"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              View Data Risk Checklists (optionnel)
              <TextInput
                value={airtableDataRiskChecklistsView}
                onChange={(event) => setAirtableDataRiskChecklistsView(event.target.value)}
                placeholder="All records"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="button button-primary button-sm" onClick={handleSaveAirtableSettings}>
              Enregistrer les paramètres Airtable
            </button>
          </div>
          {airtableSaveMessage ? (
            <div className="rounded border border-green-200 bg-green-50 p-2 text-sm text-green-800">
              {airtableSaveMessage}
            </div>
          ) : null}
          {airtableError ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-800">
              {airtableError}
            </div>
          ) : null}

          <div className="rounded border border-slate-200 bg-white p-3">
            <div className="text-base font-semibold text-slate-900">Membres & rôles</div>
            <p className="mt-1 text-sm text-slate-700">
              Gérez les accès par rôle pour ce projet de roadmap.
            </p>

            <div className="mt-3 flex flex-col gap-3">
              {roadmapMembers.length ? (
                roadmapMembers.map((member) => (
                  <div key={member.userId} className="flex flex-wrap items-center gap-2 rounded border border-slate-100 bg-slate-50 p-2">
                    <div className="min-w-[200px] text-sm text-slate-800">{member.email}</div>
                    <SelectInput
                      value={member.role}
                      onChange={(event) => void handleSaveRoadmapMember(member.userId, event.target.value as RoadmapRole)}
                    >
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </SelectInput>
                    <button
                      type="button"
                      className="button button-ghost button-sm"
                      onClick={() => void handleRemoveRoadmapMember(member.userId)}
                    >
                      Retirer
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-600">Aucun membre configuré pour ce projet.</div>
              )}
            </div>

            <div className="mt-4 grid gap-2 md:grid-cols-[2fr_1fr_auto]">
              <SelectInput value={selectedMemberUserId} onChange={(event) => setSelectedMemberUserId(event.target.value)}>
                <option value="">Ajouter un membre…</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </SelectInput>
              <SelectInput value={selectedMemberRole} onChange={(event) => setSelectedMemberRole(event.target.value as RoadmapRole)}>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </SelectInput>
              <button type="button" className="button button-secondary button-sm" onClick={handleAddRoadmapMember}>
                Ajouter
              </button>
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-800">Rôles → droits</div>
              <div className="mt-2 grid gap-2 text-xs text-slate-700 md:grid-cols-[1.2fr_repeat(4,1fr)]">
                <div className="font-semibold text-slate-600">Rôle</div>
                <div className="font-semibold text-slate-600">Lecture</div>
                <div className="font-semibold text-slate-600">Édition</div>
                <div className="font-semibold text-slate-600">Validation</div>
                <div className="font-semibold text-slate-600">Export PDF</div>
                {roleOptions.map((role) => {
                  const permissions = roadmapRolePermissions[role];
                  const badge = (allowed?: boolean) => (allowed ? "✅" : "—");
                  return (
                    <div key={role} className="contents">
                      <div className="text-slate-800">{role}</div>
                      <div>{badge(permissions?.read)}</div>
                      <div>{badge(permissions?.edit)}</div>
                      <div>{badge(permissions?.review)}</div>
                      <div>{badge(permissions?.export)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {memberSaveMessage ? (
              <div className="mt-3 rounded border border-green-200 bg-green-50 p-2 text-sm text-green-800">
                {memberSaveMessage}
              </div>
            ) : null}
            {memberError ? (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-800">{memberError}</div>
            ) : null}
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-base font-semibold text-slate-900">Generate archetype</div>
              <p className="text-sm text-slate-700">
                Create a simple app archetype (template + rails) and download a ready-to-share archive.
              </p>
            </div>
            <button
              type="button"
              className="button button-primary button-sm"
              onClick={() => void handleGenerateArchetype()}
              disabled={isGeneratingArchetype}
            >
              {isGeneratingArchetype ? "Generating..." : "Generate archetype"}
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">App name</label>
              <TextInput
                value={appNameInput}
                onChange={(event) => setAppNameInput(event.target.value)}
                placeholder="Studio Archetype App"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Description</label>
              <TextInput
                value={appDescriptionInput}
                onChange={(event) => setAppDescriptionInput(event.target.value)}
                placeholder="Sample archetype generated from Studio"
              />
            </div>
          </div>

          <div className="text-xs text-slate-600">
            Archives are written under <span className="font-mono">STUDIO_ARCHETYPE_ROOT</span> (defaults to{" "}
            <span className="font-mono">&lt;persistent_root&gt;/archetypes</span>) so you can point it to a OneDrive-friendly folder.
          </div>

          {archetypeError ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{archetypeError}</div>
          ) : null}

          {archetypeResult ? (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Latest archetype</div>
                  <div className="text-xs text-slate-600">
                    Output directory: <span className="font-mono">{archetypeResult.outputDir}</span>
                  </div>
                  <div className="text-xs text-slate-600">Pack: {archetypeResult.pack.title}</div>
                </div>
                <a className="button button-secondary button-sm" href={archetypeResult.archiveUrl} download>
                  Download archive
                </a>
              </div>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Inputs</div>
                  <ul className="list-disc space-y-1 pl-4 text-xs text-slate-700">
                    {Object.entries(archetypeResult.pack.inputs).map(([key, value]) => (
                      <li key={key}>
                        {key}: <span className="font-mono">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Acceptance</div>
                  <ul className="list-disc space-y-1 pl-4 text-xs text-slate-700">
                    {archetypeResult.pack.acceptanceTests.map((test) => (
                      <li key={test.id}>{test.description}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Smoke tests</div>
                <div className="flex flex-col gap-2">
                  {archetypeResult.smokeResults.map((result) => (
                    <div
                      key={result.id}
                      className="flex flex-wrap items-start justify-between gap-2 rounded border border-slate-200 bg-white p-2"
                    >
                      <div>
                        <div className="text-xs font-semibold text-slate-900">{result.id}</div>
                        <div className="text-xs text-slate-700">Command: {result.command.join(" ")}</div>
                        {result.message ? (
                          <div className="text-[11px] text-slate-600">Log: {result.message}</div>
                        ) : null}
                      </div>
                      <Badge
                        label={result.status === "passed" ? "Passed" : "Failed"}
                        variant={result.status === "passed" ? "success" : "danger"}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {archetypeResult.notes?.length ? (
                <div className="mt-3 flex flex-col gap-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Notes</div>
                  <ul className="list-disc space-y-1 pl-4 text-xs text-slate-700">
                    {archetypeResult.notes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      <InternalAdminSection />
    </PageShell>
  );
}
